import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch, API_BASE } from '~/lib/api';
import { Icon, WaitingInline } from '~/components/ui';
import { useT, type Messages } from '~/i18n';
import { ensureManagedMembership } from './api/onboardingClient';
import { CvUploadError, prepareCvFile, uploadCvFile } from './api/cvUploadClient';
import { saveWhatsappPhone } from './api/englishClient';
import WhatsappPhoneInput from '~/components/WhatsappPhoneInput';
import { isPhoneDraftInvalid, phoneDraftToE164 } from '~/lib/whatsappPhone';
import { invalidateOnboardingGate } from '~/components/shell/useOnboardingGate';
import ConsentStep from './components/ConsentStep';
import H2StatusPicker from './components/H2StatusPicker';
import EuAuthorizationPicker from './components/EuAuthorizationPicker';
import { saveImmigration, type ImmigrationPayload } from './api/immigrationClient';
import { buildH2Payload, type EuWorkAuthorization, type H2Status } from './lib/immigration';
import {
  isGmailPathOffered,
  isManagedChannelAvailable,
  isOnboardingComplete,
  onboardingStage,
  type OnboardingStatus,
} from './lib/onboarding';

/**
 * El alta del candidato: por dónde salen sus correos, con qué hoja de vida y,
 * para quien postula a EE. UU., su situación migratoria H-2 (una pregunta).
 *
 * ── LA RUTA DEFAULT ES EL CANAL GESTIONADO ─────────────────────────────────
 *
 * Mientras Google tenga la verificación en evaluación (CASA), el consentimiento
 * de Gmail le muestra a cualquiera una pantalla que dice "app no verificada".
 * Esa pantalla no se puede maquillar y es donde se cae la mitad de la gente, así
 * que el camino de Gmail queda ESCONDIDO detrás de `gmail_signup_enabled` — se
 * oculta, no se borra: cuando CASA pase, la bandera se prende y el CTA vuelve
 * sin tocar código. Con la bandera apagada la primera pantalla deja de ser una
 * elección y pasa a ser una bienvenida: TBit pone el correo, y listo.
 *
 * ── EL ALTA SE DIFIERE HASTA QUE HAY ALGO QUE GUARDAR ──────────────────────
 *
 * Cuando la bienvenida no ofrece alternativa, cobrarle un POST que crea la
 * membresía deja una cuenta a medias por cada persona que abrió la página y se
 * fue. Así que se activa al guardar la hoja de vida —el archivo o el
 * cuestionario—, que es el primer momento en que existe algo que poner adentro.
 */

type OAuthErrors = Messages['onboarding']['errors'];
type OAuthErrorCode = Exclude<keyof OAuthErrors, 'unknown'>;

/** El texto de un `?error=` del OAuth; un código desconocido se muestra tal cual. */
function oauthErrorText(errors: OAuthErrors, code: string): string {
  if (code !== 'unknown' && Object.hasOwn(errors, code)) return errors[code as OAuthErrorCode];
  return errors.unknown(code);
}

export default function JobApplyOnboardingPage() {
  const t = useT('onboarding');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  /** La bienvenida ya se leyó: sigue la hoja de vida aunque no haya membresía. */
  const [senderAcknowledged, setSenderAcknowledged] = useState(false);
  /** Último paso (solo con free_tier): términos + perfil público. */
  const [consentOpen, setConsentOpen] = useState(false);

  const error = params.get('error');
  const gmailConnected = params.get('gmail') === 'connected';
  const forceStep = params.get('step');

  const fetchStatus = useCallback(() => {
    setLoading(true);
    apiFetch('/job-apply/api/onboarding-status')
      .then((r) => {
        if (r.status === 401) throw new Error('not_authenticated');
        return r.json();
      })
      .then((data: OnboardingStatus) => {
        setStatus(data);
        if (isOnboardingComplete(data) && !forceStep) {
          // Con free_tier, quien aún no consintió pasa por ConsentStep antes del panel.
          if (data.free_tier === true && data.needs_consent === true) setConsentOpen(true);
          else navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [navigate, forceStep]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, gmailConnected]);

  if (loading) return <WaitingInline />;

  const stage = onboardingStage(status);

  if (consentOpen) {
    return (
      <ConsentStep
        freeTier
        onDone={() => {
          // Que una caché con needsConsent:true no vuelva a mostrar el paso.
          invalidateOnboardingGate();
          navigate('/dashboard', { replace: true });
        }}
      />
    );
  }
  const gmailOffered = isGmailPathOffered(status);
  const managedAvailable = isManagedChannelAvailable(status);

  if (stage === 'sender' && !senderAcknowledged && forceStep !== 'cv') {
    return (
      <SenderStep
        error={error}
        gmailOffered={gmailOffered}
        managedAvailable={managedAvailable}
        /* Con el camino de Gmail a la vista la elección es real y el alta ocurre
           al elegir; sin él, la bienvenida sólo avanza y la membresía nace con
           la hoja de vida. */
        activateNow={gmailOffered}
        onActivated={fetchStatus}
        onContinue={() => setSenderAcknowledged(true)}
      />
    );
  }

  // Después de la hoja de vida. Quien ya tenía CV (terminó el alta antes de
  // esta pregunta) cae acá directo: la guarda del shell lo manda a /onboarding
  // y este paso es lo único que le falta.
  if (stage === 'immigration' && forceStep !== 'cv') {
    return (
      <ImmigrationStep
        initialStatus={status?.h2_status ?? null}
        initialEu={status?.eu_work_authorization ?? null}
        askH2={status?.needs_h2_status === true}
        askEu={status?.needs_eu_authorization === true}
        onSaved={() => {
          // Después de la situación migratoria, el consentimiento (solo con free_tier).
          if (status?.free_tier === true && status.needs_consent === true) setConsentOpen(true);
          else navigate('/dashboard', { replace: true });
        }}
      />
    );
  }

  if (stage !== 'complete' || forceStep === 'cv') {
    return (
      <CVStep
        needsMembership={!status?.has_membership}
        managedAvailable={managedAvailable}
        /* Con ?step=cv (volver a subir la hoja de vida) se vuelve al panel como
           siempre. En el alta se relee el estado: si falta la situación
           migratoria, el paso sale acá mismo en vez de ir al panel y rebotar. */
        onComplete={forceStep ? () => navigate('/dashboard', { replace: true }) : fetchStatus}
      />
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="card shadow-lg max-w-md w-full rise">
        <div className="card-body items-center text-center">
          {/* Mint: es un hecho de la cuenta, no una promesa. */}
          <div
            className="grid place-items-center w-14 h-14 rounded-2xl mb-2 text-data"
            style={{ background: 'rgba(31, 237, 201, 0.16)' }}
          >
            <Icon name="check" size={26} />
          </div>
          <h2 className="card-title text-2xl">{t.done.title}</h2>
          <p className="hush">{t.done.body}</p>
          <div className="card-actions mt-4">
            <a href="/dashboard" className="btn btn-primary">
              {t.done.cta}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Paso: el correo desde el que sales ---------- */

function SenderStep({
  error,
  gmailOffered,
  managedAvailable,
  activateNow,
  onActivated,
  onContinue,
}: {
  error: string | null;
  gmailOffered: boolean;
  managedAvailable: boolean;
  activateNow: boolean;
  onActivated: () => void;
  onContinue: () => void;
}) {
  const t = useT('onboarding');
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState('');

  const proceed = useCallback(async () => {
    if (!activateNow) {
      onContinue();
      return;
    }
    setActivating(true);
    setActivationError('');
    try {
      await ensureManagedMembership();
      onActivated();
    } catch (err) {
      setActivationError(err instanceof Error ? err.message : t.sender.activationFailed);
    } finally {
      setActivating(false);
    }
  }, [activateNow, onActivated, onContinue, t]);

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="card shadow-lg max-w-md w-full rise">
        <div className="card-body items-center text-center">
          {/* La mascota entregada, no un PNG alojado en un bucket de S3 que ya
              venía con su propio `onError` para cuando no cargara. */}
          <img src="/brand/mascot-256.png" alt="" width={96} height={96} className="mb-1" />

          <h2 className="card-title text-2xl">{t.sender.title}</h2>
          <p className="text-sm hush">{t.sender.lede}</p>

          <div className="text-left w-full space-y-3 my-4">
            <div className="flex items-start gap-3">
              <span className="text-brand mt-0.5"><Icon name="send" size={18} /></span>
              <p className="text-sm hush">{t.sender.pointSend}</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-brand mt-0.5"><Icon name="mail" size={18} /></span>
              <p className="text-sm hush">{t.sender.pointReplies}</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-brand mt-0.5"><Icon name="check" size={18} /></span>
              <p className="text-sm hush">{t.sender.pointNoSpam}</p>
            </div>
          </div>

          {(error || activationError) && (
            <div className="alert alert-error text-sm w-full">
              {activationError || oauthErrorText(t.errors, error!)}
            </div>
          )}

          <div className="card-actions mt-2 w-full flex-col gap-3">
            {(managedAvailable || !gmailOffered) && (
              <>
                <button className="btn btn-primary w-full" onClick={proceed} disabled={activating}>
                  {activating && <span className="loading loading-spinner loading-sm" />}
                  {gmailOffered ? t.sender.ctaManaged : t.sender.ctaStart}
                </button>
                <p className="text-xs hush">{t.sender.ctaHint}</p>
              </>
            )}

            {/* El camino de Gmail sólo se ofrece cuando el servidor dice que se
                puede: con la bandera apagada, /auth/start responde 503 y la
                persona vería un JSON en el navegador. Esconderlo acá es el
                camino; el 503 es la red de seguridad. Reconectar un Gmail que YA
                existe no pasa por esta pantalla y no se esconde nunca. */}
            {gmailOffered && (
              <>
                <div className="divider brand-label my-1">{t.sender.or}</div>
                <a
                  href={`${API_BASE}/job-apply/auth/start`}
                  className={`btn w-full gap-2 ${managedAvailable ? 'btn-outline' : 'btn-primary'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t.sender.ctaGmail}
                </a>
              </>
            )}
          </div>

          <details
            className="collapse collapse-arrow w-full mt-4 text-left"
            style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}
          >
            <summary className="collapse-title text-sm font-medium">{t.sender.moreInfo}</summary>
            <div className="collapse-content text-xs hush space-y-2">
              <p>
                <strong>{t.sender.managedTitle}</strong> {t.sender.managedBody}
              </p>
              {gmailOffered && (
                <p>
                  <strong>{t.sender.gmailTitle}</strong> {t.sender.gmailBody}
                </p>
              )}
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

/* ---------- Paso: tu situación migratoria (H-2) ---------- */

/**
 * Hasta dos preguntas, en un solo PATCH de preferencias, y sigue al panel:
 *  - la situación H-2, a quien postula a EE. UU. y no la contestó. El envío no
 *    depende de ella: es para presentar bien a la persona;
 *  - «¿Puedes trabajar legalmente en la Unión Europea?», a quien eligió Europa
 *    y no la contestó. Ésta sí decide a qué vacantes de EURES se postula.
 */
function ImmigrationStep({
  initialStatus,
  initialEu,
  askH2,
  askEu,
  onSaved,
}: {
  initialStatus: H2Status | null;
  initialEu: EuWorkAuthorization | null;
  askH2: boolean;
  askEu: boolean;
  onSaved: () => void;
}) {
  const t = useT('onboarding').immigration;
  const [h2Status, setH2Status] = useState<H2Status | null>(initialStatus);
  const [employer, setEmployer] = useState('');
  const [euAuth, setEuAuth] = useState<EuWorkAuthorization | null>(initialEu);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useCallback(async () => {
    if (askH2 && !h2Status) {
      setError(t.pickOne);
      return;
    }
    if (askEu && !euAuth) {
      setError(t.eu.pickOne);
      return;
    }
    const payload: ImmigrationPayload = {
      ...(askH2 && h2Status ? buildH2Payload(h2Status, employer) : {}),
      ...(askEu && euAuth ? { eu_work_authorization: euAuth } : {}),
    };
    setSaving(true);
    setError(null);
    try {
      await saveImmigration(payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.saveFailed);
    } finally {
      setSaving(false);
    }
  }, [askH2, askEu, h2Status, employer, euAuth, onSaved, t]);

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="card shadow-lg max-w-md w-full rise">
        <div className="card-body">
          <header className="text-center">
            <span className="brand-label">{t.step}</span>
            <h2 className="card-title text-2xl justify-center mt-1">{t.title}</h2>
            <p className="hush text-sm mt-1">{askH2 ? t.lede : t.eu.lede}</p>
          </header>

          {askH2 && (
            <div className="mt-4">
              <H2StatusPicker
                idPrefix="onboarding"
                status={h2Status}
                employer={employer}
                disabled={saving}
                onStatus={(next) => {
                  setH2Status(next);
                  setError(null);
                }}
                onEmployer={setEmployer}
              />
            </div>
          )}

          {askEu && (
            <div className="mt-4">
              <EuAuthorizationPicker
                showQuestion
                value={euAuth}
                disabled={saving}
                onChange={(next) => {
                  setEuAuth(next);
                  setError(null);
                }}
              />
            </div>
          )}

          {error && (
            <div className="alert alert-error text-sm w-full mt-3" role="alert">
              {error}
            </div>
          )}

          <p className="text-xs hush mt-3 text-center">{askH2 ? t.note : t.eu.note}</p>

          <div className="card-actions mt-3 w-full">
            <button className="btn btn-primary w-full" onClick={save} disabled={saving}>
              {saving && <span className="loading loading-spinner loading-sm" />}
              {t.submit}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Paso: la hoja de vida, por dos puertas ---------- */

/**
 * Dos puertas de la MISMA jerarquía.
 *
 * Subir un archivo era la única entrada, y dejaba afuera a la mayoría: quien
 * aplica a una H-2B no suele tener un CV en PDF, y si lo tiene está en español
 * y armado para un empleador local. La segunda puerta no es un plan B —es la
 * que más se va a usar— así que las dos tarjetas pesan lo mismo y las dos
 * llevan un CTA púrpura.
 */
function CVStep({
  needsMembership,
  managedAvailable,
  onComplete,
}: {
  needsMembership: boolean;
  managedAvailable: boolean;
  onComplete: () => void;
}) {
  const t = useT('onboarding');
  const navigate = useNavigate();
  const [mode, setMode] = useState<'choose' | 'upload'>('choose');

  if (mode === 'upload') {
    return (
      <CVUploadStep
        needsMembership={needsMembership}
        managedAvailable={managedAvailable}
        onBack={() => setMode('choose')}
        onComplete={onComplete}
      />
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="w-full max-w-3xl space-y-5">
        <header className="text-center">
          <span className="brand-label">{t.cvGate.step}</span>
          <h2 className="text-2xl font-semibold mt-1">{t.cvGate.title}</h2>
          <p className="hush text-sm mt-1">{t.cvGate.lede}</p>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          <GateCard
            icon="doc"
            title={t.cvGate.uploadTitle}
            lede={t.cvGate.uploadLede}
            bullets={t.cvGate.uploadBullets}
            cta={t.cvGate.uploadCta}
            onClick={() => setMode('upload')}
          />

          <GateCard
            icon="sparkle"
            title={t.cvGate.buildTitle}
            lede={t.cvGate.buildLede}
            bullets={t.cvGate.buildBullets}
            cta={t.cvGate.buildCta}
            onClick={() => navigate('/onboarding/screening')}
          />
        </div>
      </div>
    </div>
  );
}

function GateCard({
  icon,
  title,
  lede,
  bullets,
  cta,
  onClick,
}: {
  icon: 'doc' | 'sparkle';
  title: string;
  lede: string;
  bullets: string[];
  cta: string;
  onClick: () => void;
}) {
  return (
    <div className="card rise h-full">
      <div className="card-body flex flex-col">
        <span
          className="grid place-items-center w-12 h-12 rounded-2xl mb-3 text-brand"
          style={{ background: 'var(--surface-hi)' }}
        >
          <Icon name={icon} size={22} />
        </span>

        <h3 className="card-title text-lg">{title}</h3>
        <p className="hush text-sm mt-1">{lede}</p>

        <ul className="space-y-2 mt-3 mb-4">
          {bullets.map((bullet) => (
            <li key={bullet} className="flex items-start gap-2 text-sm hush">
              <span className="text-brand mt-0.5 shrink-0">
                <Icon name="check" size={15} />
              </span>
              {bullet}
            </li>
          ))}
        </ul>

        <button className="btn btn-primary w-full mt-auto" onClick={onClick}>
          {cta}
        </button>
      </div>
    </div>
  );
}

/* ---------- Puerta 1: el archivo ---------- */

function CVUploadStep({
  needsMembership,
  managedAvailable,
  onBack,
  onComplete,
}: {
  needsMembership: boolean;
  managedAvailable: boolean;
  onBack: () => void;
  onComplete: () => void;
}) {
  const t = useT('onboarding');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  /** Su WhatsApp, opcional: la otra puerta (el cuestionario) también lo pide. */
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneWarning, setPhoneWarning] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Se valida y se lee a memoria al elegirlo (api/cvUploadClient.ts): si el
  // teléfono no deja abrir el archivo, se sabe acá y no al tocar «Subir».
  const handleFileSelect = useCallback(async (f: File) => {
    setError(null);
    try {
      setFile(await prepareCvFile(f));
    } catch (err) {
      setFile(null);
      setError(err instanceof Error ? err.message : t.upload.failed);
    }
  }, [t]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFileSelect(dropped);
    },
    [handleFileSelect],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) handleFileSelect(selected);
    },
    [handleFileSelect],
  );

  const upload = useCallback(async () => {
    if (!file) return;
    // Opcional, pero si lo escribió tiene que ser un número posible: se avisa
    // antes de subir, no después.
    if (isPhoneDraftInvalid(phone)) {
      setPhoneError(t.whatsapp.invalid);
      return;
    }
    setPhoneError(null);
    setPhoneWarning(null);
    setUploading(true);
    setError(null);
    try {
      // El alta diferida se cobra acá: sin membresía el upload responde 400 y el
      // archivo se pierde. Es idempotente, así que llamarlo de más no cuesta.
      if (needsMembership && managedAvailable) {
        await ensureManagedMembership();
      }

      await uploadCvFile(file);
      // Ya hay membresía (el upload la exige): el WhatsApp va a ella. Si falla,
      // el alta sigue y se puede agregar en Perfil.
      const e164 = phoneDraftToE164(phone);
      if (e164) {
        await saveWhatsappPhone(e164).catch(() => setPhoneWarning(t.whatsapp.saveFailedAfterCv));
      }
      setUploaded(true);
    } catch (err) {
      // Sin red ni al reintentar: además de qué hacer, la otra puerta del alta.
      setError(
        err instanceof CvUploadError && err.kind === 'network'
          ? `${err.message} ${t.upload.questionnaireHint(t.upload.notAtHand)}`
          : err instanceof Error ? err.message : t.upload.failed,
      );
    } finally {
      setUploading(false);
    }
  }, [file, phone, needsMembership, managedAvailable, t]);

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="card shadow-lg max-w-md w-full rise">
        <div className="card-body items-center text-center">
          <span className="brand-label mb-1">{t.upload.step}</span>

          <h2 className="card-title text-2xl">{t.upload.title}</h2>
          <p className="hush text-sm">{t.upload.lede}</p>

          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            onChange={onInputChange}
          />

          <div
            role="button"
            tabIndex={0}
            className="drop w-full"
            data-over={dragOver ? 'true' : undefined}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
            }}
          >
            <div className="flex flex-col items-center gap-1">
              <span className="drop__art"><Icon name="doc" size={24} /></span>
              {file ? (
                <>
                  <p className="font-medium text-sm">{file.name}</p>
                  <p className="text-xs hush">{(file.size / 1024).toFixed(0)} KB</p>
                </>
              ) : (
                <>
                  <p className="font-medium text-sm">{t.upload.dropFile}</p>
                  <p className="text-xs hush">{t.upload.dropHint}</p>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-error text-sm w-full mt-2">
              {error}
            </div>
          )}

          <div className="form-control w-full text-left mt-3">
            <label className="label pb-1" htmlFor="onboarding-whatsapp">
              <span className="label-text font-medium">
                {t.whatsapp.label}
                <span className="hush font-normal">{t.whatsapp.optional}</span>
              </span>
            </label>
            <WhatsappPhoneInput
              id="onboarding-whatsapp"
              value={phone}
              invalid={!!phoneError}
              disabled={uploading || uploaded}
              onChange={(value) => {
                setPhone(value);
                setPhoneError(null);
              }}
            />
            {phoneError ? (
              <p className="text-xs text-friction mt-1" role="alert">{phoneError}</p>
            ) : (
              <p className="text-xs hush mt-1">{t.whatsapp.consent}</p>
            )}
          </div>

          {!uploaded && (
            <div className="card-actions mt-4 w-full flex-col gap-2">
              <button
                className="btn btn-primary w-full"
                onClick={upload}
                disabled={!file || uploading}
              >
                {uploading && (
                  <span className="loading loading-spinner loading-sm" />
                )}
                {t.upload.submit}
              </button>
              <button className="btn btn-ghost btn-sm w-full" onClick={onBack} disabled={uploading}>
                {t.upload.notAtHand}
              </button>
            </div>
          )}

          {uploaded && (
            <div className="w-full mt-4 space-y-3">
              <div className="alert alert-success text-sm">
                <Icon name="check" />
                {t.upload.uploaded}
              </div>
              {phoneWarning && <p className="text-xs hush">{phoneWarning}</p>}
              <button className="btn btn-primary w-full" onClick={onComplete}>
                {t.upload.continue}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
